import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {createProductServiceRepository} from "../data/repositories";
import {
  PaginatedResult,
  V2PaginatedResult,
} from "../data/mappers/product-service.mapper.types";
import {ProductServiceSummary} from "../domain/product-service.types";
import {GetStoreServicesApiResponse} from "../../../react-query-hooks/store/services/get-services/types";
import {CoverImagesItemResponse} from "../../../react-query-hooks/products/types";
import {GET_STORE_SERVICES_RQ_KEY} from "../../../react-query-hooks/store/services/constants";
import {PaginationMeta} from "../../../redux/service/i-service";

/**
 * Legacy list item shape — the exact type the dashboard consumers read.
 * Aliased to the source of truth (the old get-services types) so the strangler
 * bridge is type-correct by construction rather than a hand-maintained clone.
 */
export type LegacyProductServiceListItem =
  GetStoreServicesApiResponse["productServices"][number];

/**
 * Legacy pagination meta — identical to the consumer's PaginationMeta.
 */
export type LegacyPaginationMeta = PaginationMeta;

/**
 * Legacy response shape matching the existing useGetStoreServices hook.
 */
export interface LegacyListResponse {
  data: GetStoreServicesApiResponse;
  meta: PaginationMeta;
}

/**
 * Maps a ProductServiceSummary to the legacy list item shape.
 *
 * The list endpoint is thin: it provides name, image, single price and order
 * stats. Fields the thin list genuinely does not carry (description, delivery
 * timeframe, booking questions, redirect, phone flag) are defaulted — consumers
 * that need those fetch the single service. Stats now flow through for real
 * (previously hardcoded to 0), fixing the dashboard's zeroed counts/revenue.
 * `imageUrl` is rebuilt into the `cover_images` object shape the table reads.
 */
const toLegacyListItem = (
  summary: ProductServiceSummary
): LegacyProductServiceListItem => {
  const coverImages: CoverImagesItemResponse[] = summary.imageUrl
    ? [
        {
          id: `cover-${summary.id}`,
          media_url: summary.imageUrl,
          media_type: "image",
          offering_id: summary.id,
          offering_type: "product-service",
          user_id: 0,
          createdAt: "",
          updatedAt: "",
        },
      ]
    : [];

  return {
    id: summary.id,
    userId: 0,
    name: summary.name,
    slug: "",
    tiny_url: summary.tinyUrl,
    description: "",
    cover_images: coverImages,
    delivery_timeframe_unit: "days",
    delivery_timeframe_value: 1,
    hideFromProfile: false,
    url_redirect: "",
    statusId: 0,
    request_phone_number: false,
    booking_questions: null,
    createdAt: "",
    updatedAt: "",
    deleted_at: null,
    // Summary fields — real values from the list endpoint.
    currency: summary.currency,
    amount: summary.amount,
    ordersPending: summary.ordersInProgress,
    ordersCompleted: summary.ordersCompleted,
    revenue: summary.totalRevenue,
  };
};

/**
 * Type guard to check if result is a V1 paginated result.
 */
const isV1PaginatedResult = (
  result:
    | PaginatedResult<ProductServiceSummary>
    | V2PaginatedResult<ProductServiceSummary>
): result is PaginatedResult<ProductServiceSummary> => {
  return "meta" in result;
};

/**
 * Legacy hook for fetching paginated list of product services.
 * Maintains backward compatibility with existing code.
 *
 * @deprecated Use useProductServices from features/product-services instead.
 * This hook will be removed in a future release.
 *
 * @param page - Page number (1-indexed)
 * @param per_page - Number of items per page
 * @returns React Query result with legacy response shape
 *
 * @example
 * // Old usage (deprecated):
 * const { data } = useGetStoreServicesApi(1, 10);
 * const services = data?.data.productServices;
 *
 * // New usage (recommended):
 * const { data } = useProductServices(1, 10);
 * const services = data?.data;
 */
export const useGetStoreServicesApi = (page: number = 1, per_page: number = 5) => {
  const repository = createProductServiceRepository();

  return useQuery<LegacyListResponse, AxiosError<{status: boolean; message: string}>>({
    // Keep the legacy query key so existing create/update/delete invalidations
    // (which target [GET_STORE_SERVICES_RQ_KEY, {page}]) still refresh the list.
    queryKey: [GET_STORE_SERVICES_RQ_KEY, {page}],
    queryFn: async () => {
      const result = await repository.getList({page, perPage: per_page});
      const legacyServices = result.data.map(toLegacyListItem);

      if (isV1PaginatedResult(result)) {
        // V1 result - use meta directly
        return {
          data: {productServices: legacyServices},
          meta: {
            current_page: result.meta.currentPage,
            next_page:
              result.meta.currentPage < result.meta.lastPage
                ? result.meta.currentPage + 1
                : result.meta.lastPage,
            per_page: result.meta.perPage,
            previous_page: result.meta.currentPage > 1 ? result.meta.currentPage - 1 : 1,
            total_pages: result.meta.lastPage,
          },
        };
      }

      // V2 result - construct meta from flat fields
      const totalPages = Math.ceil(result.total / per_page);
      return {
        data: {productServices: legacyServices},
        meta: {
          current_page: page,
          next_page: result.hasNextPage ? page + 1 : page,
          per_page: per_page,
          previous_page: page > 1 ? page - 1 : 1,
          total_pages: totalPages,
        },
      };
    },
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    keepPreviousData: true,
  });
};
